"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import StyledQR from "./StyledQR";
import toast from "react-hot-toast";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";
import { RootState } from "../store";
import { Loader2, AlertCircle, ExternalLink, Check, Copy } from "lucide-react";

/**
 * PyusdPaymentPage — the buyer-facing flow for "Pay with PayPal" and
 * "Pay with Venmo".  Both methods use the same protocol: a SimpleSwap
 * exchange that converts PYUSD (sent from the user's PayPal/Venmo
 * wallet) into USDC on Base, delivered to the CashTap wallet.
 *
 * Structure mirrors LightningPaymentPage.tsx deliberately — same step
 * tracker, same expiry timer, same QR + copy pattern — so the UX feels
 * consistent across the "instant fiat-ish" add-funds methods.
 *
 * The only meaningful branch is `source` (paypal vs venmo): it changes
 * the branded header copy, accent color, and the "how to" instructions
 * the buyer sees below the QR.  Everything downstream is identical.
 */

type OrderStatus =
  | "waiting"     // exchange created, no deposit yet
  | "confirming"  // deposit seen, waiting on chain confirmations
  | "exchanging"  // PYUSD → USDC swap in progress
  | "sending"     // payout tx broadcasted
  | "finished"    // funds delivered
  | "expired"
  | "failed"
  | "refunded"
  | "verifying";

type Source = "paypal" | "venmo";

interface PyusdOrder {
  exchangeId: string;
  depositAddress: string;
  depositMemo: string | null;
  pyusdAmount: string;
  usdcAmount: string;
  usdAmount: number;
  expiresAt: number | null;
  status: OrderStatus;
  source: Source;
  fromCurrency: string;
  toCurrency: string;
}

// ─── Step tracker config ────────────────────────────────────────────────────
// Step labels are deliberately web2-flavoured — no "exchange",
// "converting", "Ethereum", or "USDC" wording. The buyer reads this as
// "I'm sending money from PayPal/Venmo to my CashTap balance", not a
// crypto swap. The dynamic `sub` lines use `brandLabel` so PayPal users
// only see "PayPal" and Venmo users only see "Venmo".
const buildSteps = (brand: string): { keys: OrderStatus[]; label: string; sub: string }[] => [
  { keys: ["waiting"],               label: "Awaiting Payment",  sub: `Send from your ${brand} balance` },
  { keys: ["confirming"],             label: "Payment Received", sub: "Verifying your transaction" },
  { keys: ["exchanging", "sending"],  label: "Processing",       sub: "Adding to your CashTap balance" },
  { keys: ["finished"],               label: "Complete",         sub: "Funds added to your account" },
];

// ─── Source-specific branding ────────────────────────────────────────────────
// We render the brand's actual logotype (loaded from /public) rather
// than a coloured circle with an icon. This makes the page feel like
// it was built by PayPal/Venmo itself rather than a generic crypto
// rail. Each source declares its own full logo, short logo, accent
// colour token, and the in-app crypto-withdraw path the buyer needs.
const SOURCE_META: Record<Source, {
  brand: string;
  accent: string;
  accentBg: string;
  accentBorder: string;
  longLabel: string;
  blurb: string;
  fullLogo: string;
  shortLogo: string;
  // If `true`, render the full logo directly against the dark page
  // background (no white pill, no padding). Set this only for assets
  // whose colours already read well on dark — e.g. the PayPal full
  // logo (built-in blue letters + light icon background) — so we can
  // show the logo at a much larger size without an awkward white card
  // around it. Logos that don't carry their own contrast (Venmo's
  // wordmark logo, which is dark navy) stay inside the white pill.
  logoOnDark: boolean;
  withdrawSteps: string[];
  // Button shown above the QR. On mobile the anchor tap navigates to the
  // brand's official https universal/app link (`appUrl`), which iOS and
  // Android hand off to the installed app. A trusted, direct anchor tap is
  // the ONLY navigation iOS routes to the app — a programmatic
  // window.location redirect is treated as untrusted and falls through to
  // the browser (that was the previous bug). If the app isn't installed the
  // same link just opens the web page. On desktop the anchor opens
  // `launchUrl` in a new tab.
  //
  // We intentionally target each app's root domain — which both apps
  // reliably claim as a universal/app link — rather than a deep crypto
  // path: deep paths aren't guaranteed to be registered in the app's link
  // manifest, and an unregistered path opens the website instead of
  // launching the app. The on-screen "How to send" steps cover the
  // Crypto → PayPal USD → Send navigation once the app is open.
  appUrl: string;     // mobile: https universal link to the app (root domain)
  launchUrl: string;  // desktop: web URL opened in a new tab
  launchButton: {
    gradient: string;   // CSS gradient used as inline style
    text: string;       // label shown on the button
  };
}> = {
  paypal: {
    brand: "PayPal",
    accent: "text-[#0070BA]",
    accentBg: "bg-[#0070BA]/10",
    accentBorder: "border-[#0070BA]/25",
    longLabel: "Pay with PayPal",
    blurb: "Instant payment from your PayPal balance",
    fullLogo: "/paypal-full.png",
    shortLogo: "/paypal-short.svg",
    logoOnDark: true,
    // The PayPal-side flow uses PayPal's "PayPal USD" product name
    // (their official consumer label for what crypto folks call PYUSD).
    // We keep "Ethereum" only in the network-selection step because
    // PayPal's own withdrawal UI literally asks the user to pick a
    // network — softening it to "default network" would just confuse
    // them when they see PayPal's screen.
    withdrawSteps: [
      "Open the PayPal app",
      "Tap Crypto → PayPal USD",
      "Tap Send and choose the Ethereum network",
      "Paste the address above and send the amount shown",
    ],
    // Mobile: tapping the button navigates to paypal.com, which the PayPal
    // app claims as a universal/app link, so the installed app opens (or
    // the website loads if it isn't installed). Desktop opens the deep
    // PYUSD crypto page in a new tab.
    appUrl: "https://www.paypal.com/",
    launchUrl: "https://www.paypal.com/myaccount/crypto/pyusd",
    launchButton: {
      gradient: "linear-gradient(135deg, #003087 0%, #0070BA 50%, #009CDE 100%)",
      text: "Open PayPal",
    },
  },
  venmo: {
    brand: "Venmo",
    accent: "text-[#3D95CE]",
    accentBg: "bg-[#3D95CE]/10",
    accentBorder: "border-[#3D95CE]/25",
    longLabel: "Pay with Venmo",
    blurb: "Instant payment from your Venmo balance",
    fullLogo: "/Venmo_full.png",
    shortLogo: "/venmo.png",
    logoOnDark: false,
    withdrawSteps: [
      "Open the Venmo app",
      "Tap Crypto → PayPal USD",
      "Tap Send and choose the Ethereum network",
      "Paste the address above and send the amount shown",
    ],
    // Mobile: navigate to venmo.com, the domain the Venmo app registers
    // as a universal/app link. Bare `venmo://` has no home route and a deep
    // `venmo://crypto/...` path triggers the store update flow, so neither
    // reliably opens the app. The installed app opens, or the website loads
    // if it isn't installed. Desktop opens the deep crypto page in a tab.
    appUrl: "https://venmo.com/",
    launchUrl: "https://account.venmo.com/crypto/pyusd",
    launchButton: {
      gradient: "linear-gradient(135deg, #0074DE 0%, #3D95CE 100%)",
      text: "Open Venmo",
    },
  },
};

// EIP-681 payment URI for PYUSD on Ethereum. Compatible wallets
// (Rainbow, Trust, MetaMask Mobile, etc.) parse this and pre-fill both
// the recipient address AND the transfer amount. Wallets that don't
// understand EIP-681 (PayPal, Venmo, most CEX apps) gracefully fall
// back to extracting just the address, so the buyer can still pay the
// "old way" by typing the amount manually. The plain-text address card
// below the QR is the universal fallback for any wallet that ignores
// the URI entirely.
//
// PYUSD is an ERC-20 token, so the URI format is:
//   ethereum:<token_contract>@<chain_id>/transfer?address=<recipient>&uint256=<amount_in_base_units>
//
// PYUSD has 6 decimals, so $5 = 5_000_000 base units.
const PYUSD_ETH_CONTRACT = "0x6c3ea9036406852006290770BEdFcAbA0e23A0e8";
const PYUSD_DECIMALS = 6;
const ETHEREUM_CHAIN_ID = 1;

function buildPyusdEthPaymentUri(toAddress: string, amountUsd: string | number | undefined): string {
  // Sanity-check inputs. If anything is missing or invalid we fall
  // back to the plain address — at worst the buyer types the amount
  // manually, which is what they were already doing pre-EIP-681.
  if (!toAddress) return "";
  const amt = typeof amountUsd === "string" ? parseFloat(amountUsd) : (amountUsd ?? 0);
  if (!Number.isFinite(amt) || amt <= 0) return toAddress;
  // Convert dollars to PYUSD base units. Math.round at the multiplier
  // step avoids floating-point dust (e.g. 5.85 * 1e6 = 5849999.999...).
  const baseUnits = BigInt(Math.round(amt * 10 ** PYUSD_DECIMALS));
  return `ethereum:${PYUSD_ETH_CONTRACT}@${ETHEREUM_CHAIN_ID}/transfer?address=${toAddress}&uint256=${baseUnits.toString()}`;
}

// Skeleton placeholder block. Renders an empty rounded surface with a
// gentle gradient shimmer sweeping across it. Pairs with the
// `shimmer` keyframe in tailwind.config.js. Dimensions are passed
// through `className` so the skeleton can match the real element's
// footprint and the layout doesn't shift when content loads (CLS-safe).
function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-xl bg-gray-800/40 ${className}`}>
      <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.06] to-transparent animate-[shimmer_1.6s_ease-in-out_infinite]" />
    </div>
  );
}

function formatUsd(value: string | number | undefined): string {
  if (!value) return "0";
  const num = parseFloat(String(value));
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ────────────────────────────────────────────────────────────────────────────

export default function PyusdPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeId: urlExchangeId } = useParams<{ exchangeId?: string }>();
  const [searchParams] = useSearchParams();
  const sourceParam = (searchParams.get("source") as Source) || "paypal";

  const state = location.state as
    | { amount: number; toAddress: string; source?: Source }
    | null;
  const userEmail = useSelector((s: RootState) => (s as any).user?.email) as string | undefined;

  const [order, setOrder] = useState<PyusdOrder | null>(null);
  // QR is rendered live by <StyledQR> from the encoded value, so the tab
  // toggle is a pure prop swap — no pre-generated data URLs needed:
  //   • with-amount  — EIP-681 URI; compatible wallets auto-fill recipient + amount
  //   • address-only — bare deposit address; universal fallback for any QR scanner
  // Default to "With amount" so Web3-aware wallets (Trust, Rainbow,
  // MetaMask Mobile, Phantom for Solana-PYUSD, etc.) parse the EIP-681
  // / Solana Pay URI and pre-fill BOTH recipient and amount. The user
  // explicitly wants the auto-fill behavior front-and-center; buyers
  // using PayPal / Venmo in-app (which don't honour the URI) can still
  // scan it — those apps fall back to the bare address and prompt the
  // user to type the amount, or the buyer can flip to "Address only".
  const [qrMode, setQrMode] = useState<"with-amount" | "address-only">("with-amount");
  const [creating, setCreating] = useState(true);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const firestoreId = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const source: Source = order?.source || state?.source || sourceParam;
  const meta = SOURCE_META[source] || SOURCE_META.paypal;

  // Auto-scroll to top on mount — Layout's <main> is the scrollable
  // container, not window.
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Redirect away if we don't have either an in-flight order id (URL)
  // or amount/toAddress (fresh creation state).
  useEffect(() => {
    if (!state?.amount && !urlExchangeId) {
      navigate("/add-funds", { replace: true });
    }
  }, [state, urlExchangeId, navigate]);

  // ─── Load existing order from Firestore (refresh / shared-link) ───────────
  const loadOrderFromFirestore = useCallback(async (id: string) => {
    setCreating(true);
    try {
      const q = query(collection(db, "pyusdOrders"), where("exchangeId", "==", id));
      const snap = await getDocs(q);
      if (snap.empty) throw new Error("Order not found");
      const data = snap.docs[0].data();
      firestoreId.current = snap.docs[0].id;
      const loaded: PyusdOrder = {
        exchangeId:     data.exchangeId,
        depositAddress: data.depositAddress,
        depositMemo:    data.depositMemo ?? null,
        pyusdAmount:    data.pyusdAmount,
        usdcAmount:     data.usdcAmount,
        usdAmount:      data.usdAmount,
        expiresAt:      data.expiresAt ?? null,
        status:         data.status,
        source:         (data.source as Source) || "paypal",
        fromCurrency:   data.fromCurrency,
        toCurrency:     data.toCurrency,
      };
      setOrder(loaded);
    } catch (e: any) {
      setError(e.message || "Could not load order");
    } finally {
      setCreating(false);
    }
  }, []);

  // ─── Create a fresh exchange ──────────────────────────────────────────────
  const createOrder = useCallback(async () => {
    if (!state?.amount || !state?.toAddress) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_SERVERURL}/pyusd/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
        },
        body: JSON.stringify({
          amount: state.amount,
          toAddress: state.toAddress,
          email: userEmail,           // beta gate runs against this
          source,                     // paypal | venmo (cosmetic only)
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create exchange");

      const o = data as PyusdOrder;
      // Sanity-check the response before touching navigation or
      // Firestore. If the backend ever regresses (or SimpleSwap changes
      // their response shape again) we want to fail loudly with a clear
      // error rather than silently writing a Firestore doc with
      // `exchangeId: undefined` that nothing will ever be able to look
      // up again.
      if (!o.exchangeId || !o.depositAddress) {
        console.error("[pyusd] backend returned malformed exchange:", o);
        throw new Error(
          "Exchange created but the response is missing required fields. " +
          "Try again — if this keeps happening, check the backend logs."
        );
      }
      setOrder(o);
      navigate(`/add-funds/pyusd/${o.exchangeId}?source=${source}`, {
        replace: true,
        state,
      });

      const docRef = await addDoc(collection(db, "pyusdOrders"), {
        walletAddress:  state.toAddress.toLowerCase(),
        exchangeId:     o.exchangeId,
        depositAddress: o.depositAddress,
        depositMemo:    o.depositMemo ?? null,
        pyusdAmount:    o.pyusdAmount ?? null,
        usdcAmount:     o.usdcAmount ?? null,
        usdAmount:      state.amount,
        expiresAt:      o.expiresAt ?? null,
        status:         o.status || "waiting",
        source:         o.source || source,
        fromCurrency:   o.fromCurrency ?? null,
        toCurrency:     o.toCurrency ?? null,
        createdAt:      serverTimestamp(),
      });
      firestoreId.current = docRef.id;
    } catch (e: any) {
      setError(e.message || "Could not create exchange");
    } finally {
      setCreating(false);
    }
  }, [state, navigate, userEmail, source]);

  useEffect(() => {
    if (urlExchangeId) {
      loadOrderFromFirestore(urlExchangeId);
    } else if (state?.amount) {
      createOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Expiry countdown ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!order?.expiresAt) return;
    const tick = () =>
      setSecondsLeft(Math.max(0, order.expiresAt! - Math.floor(Date.now() / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [order?.expiresAt]);

  // ─── Poll status every 5s until terminal state ────────────────────────────
  const TERMINAL: OrderStatus[] = ["finished", "expired", "failed", "refunded"];
  useEffect(() => {
    if (!order || TERMINAL.includes(order.status)) return;
    const poll = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SERVERURL}/pyusd/status/${order.exchangeId}`,
          {
            headers: { Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` },
          },
        );
        // SimpleSwap returns 404 "Exchange not found" when the id is
        // unknown to the current API key (e.g. created under a Default
        // key, polled under a DeFi key after a key swap). Treat this as
        // terminal — stop polling and surface a "this exchange is no
        // longer reachable" state so the user can start fresh instead
        // of watching the spinner forever.
        if (res.status === 404) {
          setOrder((p) => (p ? { ...p, status: "failed" } : p));
          if (firestoreId.current) {
            updateDoc(doc(db, "pyusdOrders", firestoreId.current), { status: "failed" }).catch(() => {});
          }
          toast.error("This exchange is no longer reachable. Please start a new one.");
          return;
        }
        const data = await res.json();
        if (data.status && data.status !== order.status) {
          const s = data.status as OrderStatus;
          setOrder((p) => (p ? { ...p, status: s } : p));
          if (firestoreId.current) {
            updateDoc(doc(db, "pyusdOrders", firestoreId.current), { status: s }).catch(() => {});
          }
          if (s === "finished") toast.success("Funds added to your wallet!");
          if (s === "expired")  toast.error("Exchange expired before payment arrived.");
          if (s === "failed")   toast.error("Exchange failed. Funds may be refundable.");
          if (s === "refunded") toast.error("Funds refunded.");
        }
      } catch {/* silent — keep polling on transient errors */}
    };
    pollRef.current = setInterval(poll, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  // Platform detection drives where the "Open PayPal/Venmo" button points.
  // On mobile the button is a plain anchor to the brand's https universal
  // link (`appUrl`), so a trusted tap lets the OS hand off to the installed
  // app; on desktop it opens the deep web page (`launchUrl`) in a new tab.
  // We deliberately do NOT intercept the click with window.location on
  // mobile — a programmatic redirect is an untrusted navigation that iOS
  // refuses to route to the app, which was the bug this replaced.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(ua);

  const copyAddress = () => {
    if (!order?.depositAddress) return;
    navigator.clipboard.writeText(order.depositAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Address copied");
  };

  // Steps are built per-render so we can interpolate the active source's
  // brand label ("Send from your PayPal balance" vs "Venmo balance")
  // without exposing the buyer to the cross-brand "PayPal / Venmo"
  // wording the previous static array used.
  const steps = buildSteps(meta.brand);
  const stepIndex = order
    ? steps.findIndex((step) => step.keys.includes(order.status))
    : 0;

  // Countdown derivatives — kept here so the prominent countdown card
  // and the small header chip both read from the same source of truth.
  // `isUrgent` flips when less than 5 min remains, which we use to
  // escalate the visual styling (red pulse, brighter border).
  const mins = String(Math.floor(Math.max(0, secondsLeft) / 60)).padStart(2, "0");
  const secs = String(Math.max(0, secondsLeft) % 60).padStart(2, "0");
  const isUrgent = secondsLeft > 0 && secondsLeft < 5 * 60;

  // ─── Render ──────────────────────────────────────────────────────────────
  if (creating) {
    // Skeleton that mirrors the final 2-column grid (CashApp-Pay-style)
    // exactly — page header, then left action column and right sticky
    // tracker. Each shimmer block matches the real element's footprint
    // so the page doesn't reflow when content lands (Core Web Vitals
    // CLS = 0). The brand pill stays "live" (not shimmery) so the
    // buyer can tell at-a-glance which flow they're in even while the
    // rest of the screen is loading.
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] px-4 py-6 sm:py-10">
        <div className="max-w-5xl mx-auto">
          {/* Page header — same shape as the live state's header so it
              snaps into place when content arrives. Brand logo pill is
              real (not a shimmer) for instant recognition. */}
          <div className="mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4">
            {meta.logoOnDark ? (
              <img
                src={meta.fullLogo}
                alt={meta.brand}
                className="h-11 sm:h-14 w-auto rounded-xl object-contain shrink-0"
              />
            ) : (
              <div className="h-14 sm:h-16 px-4 rounded-xl bg-white flex items-center shrink-0 shadow-sm">
                <img
                  src={meta.fullLogo}
                  alt={meta.brand}
                  className="h-7 sm:h-8 w-auto object-contain"
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                  {meta.longLabel}
                </h1>
                <Loader2 className={`w-4 h-4 animate-spin ${meta.accent}`} />
              </div>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                Connecting to {meta.brand} · this usually takes a few seconds
              </p>
            </div>
          </div>

          {/* Same 2-col grid as the live state */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 lg:gap-6 items-start">
            {/* LEFT column skeleton */}
            <div className="space-y-3 sm:space-y-4">
              {/* Amount summary skeleton */}
              <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-2">
                    <Shimmer className="h-2.5 w-28 rounded-md" />
                    <Shimmer className="h-8 sm:h-10 w-24 rounded-lg" />
                  </div>
                  <div className="space-y-2 items-end flex flex-col">
                    <Shimmer className="h-2.5 w-20 rounded-md" />
                    <Shimmer className="h-4 w-16 rounded-md" />
                  </div>
                </div>
              </div>

              {/* QR card skeleton */}
              <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
                {/* Timer strip placeholder */}
                <div className="flex items-center justify-between px-4 sm:px-5 py-2.5 border-b border-gray-800/60">
                  <Shimmer className="h-3 w-28 rounded-md" />
                  <Shimmer className="h-2.5 w-16 rounded-md" />
                </div>

                <div className="p-4 sm:p-6 flex flex-col items-center gap-4">
                  {/* QR toggle placeholder */}
                  <Shimmer className="h-8 w-48 rounded-xl" />

                  {/* QR placeholder — same dimensions as the real QR */}
                  <div className="p-3 sm:p-3.5 bg-white/5 rounded-2xl">
                    <Shimmer className="w-44 h-44 sm:w-48 sm:h-48 rounded-xl !bg-gray-800/30" />
                  </div>

                  <Shimmer className="h-3 w-56 rounded-md" />

                  {/* Address row placeholder */}
                  <div className="w-full mt-1 space-y-1.5">
                    <Shimmer className="h-2.5 w-32 mx-auto rounded-md" />
                    <Shimmer className="h-11 w-full rounded-xl" />
                  </div>

                  {/* Launch button placeholder */}
                  <Shimmer className="h-12 w-full rounded-2xl" />
                </div>
              </div>
            </div>

            {/* RIGHT column skeleton (sticky on lg+) */}
            <div className="space-y-3 sm:space-y-4 lg:sticky lg:top-6">
              {/* Payment Status card skeleton */}
              <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-800/60">
                  <Shimmer className="h-3 w-28 rounded-md" />
                  <Shimmer className="h-4 w-16 rounded-full" />
                </div>

                <div className="px-4 sm:px-5 py-4 sm:py-5">
                  {/* Progress bar */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1.5">
                      <Shimmer className="h-2.5 w-14 rounded-md" />
                      <Shimmer className="h-2.5 w-8 rounded-md" />
                    </div>
                    <Shimmer className="h-1.5 w-full rounded-full" />
                  </div>

                  {/* Step rows */}
                  <div className="space-y-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2.5"
                      >
                        <Shimmer className="w-7 h-7 rounded-full shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <Shimmer className="h-3 w-28 rounded-md" />
                          <Shimmer className="h-2.5 w-40 rounded-md" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* How to pay skeleton */}
              <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl p-4">
                <Shimmer className="h-3 w-36 rounded-md mb-3" />
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-2">
                      <Shimmer className="w-3 h-3 rounded-full mt-1.5" />
                      <Shimmer className="h-3 flex-1 rounded-md" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Safety note skeleton */}
              <div className="bg-amber-500/5 border border-amber-500/15 rounded-2xl p-3 flex items-start gap-2">
                <Shimmer className="w-3.5 h-3.5 rounded-full mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <Shimmer className="h-2.5 w-full rounded-md" />
                  <Shimmer className="h-2.5 w-3/4 rounded-md" />
                </div>
              </div>

              <div className="flex justify-center">
                <Shimmer className="h-2.5 w-40 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Most likely cause of the "Order not found" path is a stale URL —
    // an exchange id from a previous attempt that no longer exists on
    // SimpleSwap (e.g., after an API-key swap, or expired exchanges
    // that got garbage-collected on their side). Don't dead-end the
    // user; offer two clear paths forward.
    const isStaleOrder = /not found/i.test(error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] flex items-center justify-center px-4">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-red-500/30 rounded-3xl p-8 max-w-md w-full text-center">
          <p className="text-red-400 font-semibold mb-2">
            {isStaleOrder ? "Exchange not available" : "Could not start exchange"}
          </p>
          <p className="text-gray-400 text-sm mb-5">
            {isStaleOrder
              ? "This exchange link is no longer valid. Start a new payment to continue."
              : error}
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate("/add-funds")}
              className="px-5 py-2.5 rounded-xl bg-[#61CE70] hover:bg-[#72D981] text-black font-bold text-sm transition-colors"
            >
              Start a new payment
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2 text-xs text-gray-400 hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const isTerminal = TERMINAL.includes(order.status);
  const isFinished = order.status === "finished";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] px-3 sm:px-4 py-3 sm:py-10">
      <div className="max-w-5xl mx-auto">
        {/* ── Page header — matches the CashApp Pay header shape so the
            two flows feel like siblings. Brand pill sized to render the
            full wordmark legibly (PayPal/Venmo are wordmark logos, not
            icon-only marks, so under ~28–32px tall they become hard to
            read). The title + subtitle live in a second flex column,
            which keeps the subtitle's left edge naturally aligned with
            the title without any fragile pixel-offset margin. */}
        <div className="hidden lg:flex mb-8 items-center gap-4">
          {meta.logoOnDark ? (
            <img
              src={meta.fullLogo}
              alt={meta.brand}
              className="h-11 sm:h-14 rounded-xl w-auto object-contain shrink-0"
            />
          ) : (
            <div className="h-14 sm:h-16 px-4 rounded-xl bg-white flex items-center shrink-0 shadow-sm">
              <img
                src={meta.fullLogo}
                alt={meta.brand}
                className="h-7 sm:h-8 w-auto object-contain"
              />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
              {meta.longLabel}
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              Scan the QR code or open the {meta.brand} app to complete your payment
            </p>
          </div>
        </div>

        {/* ── Two-column grid — left holds the action surface (amount,
            QR, address, launch button); right rail is a sticky tracker
            (status + steps + how-to + safety). Mirrors the CashApp Pay
            page layout so buyers who've used both feel at home. */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 lg:gap-6 items-start">

          {/* ──────────────── LEFT COLUMN ────────────────
              On mobile/tablet (< lg) this whole column collapses into ONE
              bordered card — brand header, amount, timer, QR, address and
              launch button all live inside it. On desktop (lg+) the wrapper
              goes chrome-less and the original stacked cards return. */}
          <div className="space-y-0 border border-gray-700/60 rounded-2xl overflow-hidden bg-[#0F1419] lg:space-y-4 lg:border-0 lg:rounded-none lg:overflow-visible lg:bg-transparent">

            {/* Mobile: in-card brand header — logo + label on a single row */}
            <div className="lg:hidden flex items-center justify-center gap-2 px-3 py-2 border-b border-gray-800/60">
              {meta.logoOnDark ? (
                <img src={meta.fullLogo} alt={meta.brand} className="h-6 w-auto object-contain shrink-0" />
              ) : (
                <div className="h-6 px-2 rounded-md bg-white flex items-center shadow-sm shrink-0">
                  <img src={meta.fullLogo} alt={meta.brand} className="h-3.5 w-auto object-contain" />
                </div>
              )}
              <p className="text-gray-400 text-[11px] font-medium whitespace-nowrap">
                Pay with {meta.brand} <span className="text-gray-600">· via CashTap</span>
              </p>
            </div>

            {/* Amount summary — money story at a glance. "You receive" is
                the bigger number because that's what lands in the
                buyer's CashTap account. "You'll send" is secondary,
                annotated with the small service-fee delta so the fee
                is never hidden. */}
            <div className="p-4 sm:p-5 border-b border-gray-800/60 lg:border lg:border-gray-800/60 lg:rounded-2xl lg:bg-[#0F1419]">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    Added to your CashTap
                  </p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tabular-nums leading-none">
                    ${formatUsd(order.usdcAmount)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                    You'll send
                  </p>
                  <p className="text-sm sm:text-base font-bold text-white tabular-nums">
                    ${formatUsd(order.pyusdAmount)}
                  </p>
                  {(() => {
                    const send = parseFloat(String(order.pyusdAmount || "0"));
                    const recv = parseFloat(String(order.usdcAmount  || "0"));
                    const fee  = Math.max(0, send - recv);
                    if (!Number.isFinite(fee) || fee <= 0) return null;
                    return (
                      <p className="text-[10px] text-gray-500 mt-1 tabular-nums">
                        incl. ${formatUsd(fee.toFixed(2))} fee
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* QR + Address + Launch button card */}
            <div className="overflow-hidden lg:bg-[#0F1419] lg:border lg:border-gray-800/60 lg:rounded-2xl">

              {/* Timer strip — sits at the top of the QR card so the
                  countdown is anchored next to the action surface, not
                  floating in its own card. Visual treatment is
                  deliberately prominent (gradient background + pulsing
                  live-dot + bold tabular numerals) because the previous
                  muted gray version read as "info chip" and buyers
                  routinely missed it. Amber by default (time-limited
                  warning), escalates to red when < 5 min remain, and
                  fades to a flat red on expiry. */}
              {!isTerminal && order.expiresAt && (
                <div
                  className={`relative flex items-center justify-between gap-3 px-4 sm:px-5 py-2.5 sm:py-3 border-b overflow-hidden ${
                    secondsLeft === 0
                      ? "border-red-500/30 bg-red-500/10"
                      : isUrgent
                        ? "border-red-500/40 bg-gradient-to-r from-red-500/20 via-red-500/10 to-transparent"
                        : "border-amber-500/40 bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                    {/* Live-dot — ping halo around a solid core. Same
                        pattern Lightning + Coinbase pages use, so the
                        "this is actively counting down" cue is consistent
                        across the app. */}
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span
                        className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${
                          isUrgent || secondsLeft === 0 ? "bg-red-400" : "bg-amber-400"
                        }`}
                      />
                      <span
                        className={`relative inline-flex rounded-full h-2 w-2 ${
                          isUrgent || secondsLeft === 0 ? "bg-red-400" : "bg-amber-400"
                        }`}
                      />
                    </span>
                    <span
                      className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider shrink-0 ${
                        isUrgent || secondsLeft === 0 ? "text-red-300" : "text-amber-300"
                      }`}
                    >
                      {secondsLeft === 0 ? "Window expired" : "Expires in"}
                    </span>
                    {secondsLeft > 0 && (
                      <span
                        className={`tabular-nums font-black text-sm sm:text-base leading-none ${
                          isUrgent ? "text-red-100" : "text-amber-100"
                        }`}
                      >
                        {mins}:{secs}
                      </span>
                    )}
                  </div>
                  <span className="hidden sm:inline text-gray-500 text-[10px] font-medium shrink-0">
                    Est. 2–5 min
                  </span>
                </div>
              )}

              <div className="p-3 min-[400px]:p-4 sm:p-6 flex flex-col items-center gap-3 sm:gap-4">

                {/* QR — two variants, swap on toggle */}
                {!isFinished && order.depositAddress && (
                  <>
                    {/* Tab toggle (SimpleSwap-style) */}
                    <div className="inline-flex bg-gray-800/60 border border-gray-700/50 rounded-xl p-1" role="tablist" aria-label="QR code format">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={qrMode === "with-amount"}
                        onClick={() => setQrMode("with-amount")}
                        className={`px-3.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${
                          qrMode === "with-amount"
                            ? "bg-white text-gray-900 shadow"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        With amount
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={qrMode === "address-only"}
                        onClick={() => setQrMode("address-only")}
                        className={`px-3.5 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${
                          qrMode === "address-only"
                            ? "bg-white text-gray-900 shadow"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        Address only
                      </button>
                    </div>

                    {/* QR with green halo, matching the Wallet QR's
                        visual treatment + the CashApp Pay QR's frame. */}
                    <div className="relative">
                      <div
                        className="absolute inset-0 rounded-2xl blur-xl opacity-40"
                        style={{
                          background: "rgba(97,206,112,0.35)",
                          transform: "scale(0.9) translateY(4px)",
                        }}
                      />
                      <div
                        className="relative p-2 min-[400px]:p-3 sm:p-3.5 bg-white rounded-2xl"
                        style={{
                          boxShadow:
                            "0 0 0 1.5px rgba(97,206,112,0.35), 0 8px 32px rgba(0,0,0,0.5), 0 0 24px rgba(97,206,112,0.12)",
                        }}
                      >
                        <StyledQR
                          logo="none"
                          accentColor="#0070BA"
                          value={
                            qrMode === "with-amount"
                              ? buildPyusdEthPaymentUri(order.depositAddress, order.pyusdAmount)
                              : order.depositAddress
                          }
                          className="w-32 h-32 max-[379px]:w-28 max-[379px]:h-28 min-[400px]:w-36 min-[400px]:h-36 sm:w-44 sm:h-44"
                        />
                      </div>
                    </div>

                    <p className="text-gray-500 text-[11px] text-center max-w-xs -mt-1">
                      {qrMode === "with-amount"
                        ? `Scan to send $${formatUsd(order.pyusdAmount)} — no typing needed`
                        : `Scan and enter $${formatUsd(order.pyusdAmount)} in ${meta.brand}`}
                    </p>

                    {/* Address row — compact copyable line, matches the
                        Lightning Invoice row from CashApp Pay. */}
                    <div className="w-full mt-1">
                      <p className="text-[10px] text-gray-600 uppercase tracking-wider font-semibold mb-1.5 text-center">
                        Send to this address
                      </p>
                      <button
                        onClick={copyAddress}
                        aria-label={copied ? "Copied" : "Copy address"}
                        className="w-full flex items-start gap-2 bg-gray-900/60 border border-gray-700/50 hover:border-[#61CE70]/30 hover:bg-gray-900/80 rounded-xl px-2.5 py-2 sm:px-3.5 sm:py-2.5 max-[379px]:px-2 max-[379px]:py-1.5 [@media(max-height:650px)]:py-1.5 text-left transition-all duration-150 group cursor-pointer"
                      >
                        <span className="text-[9px] min-[400px]:text-[10px] sm:text-[11px] font-mono text-gray-400 flex-1 min-w-0 break-all leading-relaxed">
                          {order.depositAddress}
                        </span>
                        <span className="shrink-0 mt-0.5">
                          {copied ? (
                            <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#61CE70]" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 group-hover:text-gray-300" />
                          )}
                        </span>
                      </button>
                      {order.depositMemo && (
                        <p className="text-[11px] text-amber-300 mt-2 text-center">
                          ⚠ Memo / tag required: <span className="font-mono">{order.depositMemo}</span>
                        </p>
                      )}
                    </div>

                    {/* Launch button — the brand short logo sits inside
                        a small white tile so it always reads against the
                        gradient (Venmo's short logo is blue, so without
                        the tile it disappears against the blue button
                        background). PayPal's tile is white for the same
                        reason, even though its logo carries more
                        built-in contrast. */}
                    <a
                      href={isMobileDevice ? meta.appUrl : meta.launchUrl}
                      target={isMobileDevice ? "_self" : "_blank"}
                      rel={isMobileDevice ? undefined : "noopener noreferrer"}
                      className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 max-[379px]:py-2 [@media(max-height:650px)]:py-2 rounded-2xl font-bold text-[13px] sm:text-sm text-white transition-all duration-150 active:scale-[0.98] cursor-pointer shadow-lg"
                      style={{ background: meta.launchButton.gradient }}
                    >
                      <span className="h-8 w-8 sm:h-9 sm:w-9 max-[379px]:h-7 max-[379px]:w-7 [@media(max-height:650px)]:h-7 [@media(max-height:650px)]:w-7 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-inner">
                        <img
                          src={meta.shortLogo}
                          alt=""
                          aria-hidden="true"
                          className="h-6 w-6 sm:h-7 sm:w-7 max-[379px]:h-5 max-[379px]:w-5 [@media(max-height:650px)]:h-5 [@media(max-height:650px)]:w-5 object-contain"
                        />
                      </span>
                      <span>{meta.launchButton.text}</span>
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 opacity-80" />
                    </a>
                  </>
                )}

                {/* Finished — celebratory complete state, same vibe as
                    CashApp Pay's done state. */}
                {isFinished && (
                  <div className="flex flex-col items-center gap-5 py-6 sm:py-8 w-full text-center">
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 rounded-full bg-[#61CE70]/20 animate-ping" />
                      <div className="relative w-20 h-20 rounded-full bg-[#61CE70]/15 border border-[#61CE70]/30 flex items-center justify-center">
                        <Check className="w-10 h-10 text-[#61CE70]" strokeWidth={2.5} />
                      </div>
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-white">Payment complete</p>
                      <p className="text-gray-400 text-sm mt-1.5">
                        ${formatUsd(order.usdcAmount)} added to your CashTap balance
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/wallet")}
                      className="px-7 sm:px-8 py-3.5 rounded-2xl font-bold text-sm transition-all duration-150 active:scale-[0.98]"
                      style={{ background: "linear-gradient(135deg, #61CE70, #50B060)", color: "#000" }}
                    >
                      View Wallet
                    </button>
                  </div>
                )}

                {/* Other terminal states — expired / failed / refunded */}
                {isTerminal && !isFinished && (
                  <div className="flex flex-col items-center gap-4 py-6 text-center w-full">
                    <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                      <AlertCircle className="w-8 h-8 text-red-400" />
                    </div>
                    <p className="text-red-400 font-semibold">
                      {order.status === "expired"
                        ? "This payment window expired"
                        : order.status === "refunded"
                          ? "Your payment was refunded"
                          : "Payment could not be completed"}
                    </p>
                    <button
                      onClick={() => navigate("/add-funds")}
                      className="px-5 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-sm font-medium transition-colors"
                    >
                      Start a new payment
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ──────────────── RIGHT COLUMN (sticky) ──────────────── */}
          <div className="space-y-3 sm:space-y-4 lg:sticky lg:top-6">

            {/* Payment Status card */}
            <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-800/60">
                <p className="text-xs font-bold text-white uppercase tracking-widest">Payment Status</p>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    isFinished
                      ? "bg-[#61CE70]/15 text-[#61CE70]"
                      : isTerminal
                        ? "bg-red-500/15 text-red-400"
                        : "bg-amber-500/10 text-amber-400"
                  }`}
                >
                  {isFinished
                    ? "Complete"
                    : isTerminal
                      ? order.status
                      : steps[stepIndex]?.label || "Pending"}
                </span>
              </div>

              <div className="px-4 sm:px-5 py-4 sm:py-5">
                {/* Progress bar */}
                <div className="mb-5">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1.5">
                    <span>Progress</span>
                    <span
                      className="font-semibold"
                      style={{ color: stepIndex >= 3 ? "#61CE70" : undefined }}
                    >
                      {Math.round(((stepIndex + 1) / steps.length) * 100)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${Math.round(((stepIndex + 1) / steps.length) * 100)}%`,
                        background: isTerminal && !isFinished
                          ? "#ef4444"
                          : "linear-gradient(90deg, #61CE70, #50B060)",
                        boxShadow:
                          stepIndex >= 0 && !(isTerminal && !isFinished)
                            ? "0 0 8px rgba(97,206,112,0.5)"
                            : undefined,
                      }}
                    />
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-1">
                  {steps.map((step, i) => {
                    const done = stepIndex > i;
                    const active = stepIndex === i;
                    return (
                      <div
                        key={step.label}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${
                          active
                            ? "bg-[#61CE70]/8 border border-[#61CE70]/15"
                            : done
                              ? "border border-transparent"
                              : "border border-transparent opacity-40"
                        }`}
                      >
                        <div className="relative shrink-0">
                          {active && (
                            <span className="absolute inset-0 rounded-full bg-[#61CE70]/25 animate-ping" />
                          )}
                          <div
                            className={`relative w-7 h-7 rounded-full flex items-center justify-center z-10 transition-all duration-500 ${
                              done
                                ? "bg-[#61CE70]"
                                : active
                                  ? "bg-[#61CE70]/15 border-2 border-[#61CE70]"
                                  : "bg-gray-800/80 border border-gray-700/60"
                            }`}
                          >
                            {done ? (
                              <Check className="w-3.5 h-3.5 text-black" strokeWidth={2.5} />
                            ) : (
                              <span className={`text-xs font-bold ${active ? "text-[#61CE70]" : "text-gray-600"}`}>
                                {i + 1}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-xs sm:text-[13px] font-semibold leading-tight ${
                              done ? "text-[#61CE70]" : active ? "text-white" : "text-gray-500"
                            }`}
                          >
                            {step.label}
                          </p>
                          <p
                            className={`text-[10px] sm:text-xs mt-0.5 ${
                              done ? "text-[#61CE70]/50" : active ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {done ? "Completed" : step.sub}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* How to pay */}
            {!isTerminal && (
              <div className="bg-[#0F1419] border border-gray-800/60 rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-3">
                  How to send from {meta.brand}
                </p>
                <ol className="space-y-2 text-xs sm:text-[13px] text-gray-300">
                  {meta.withdrawSteps.map((stepText, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[#61CE70] font-bold shrink-0">{i + 1}.</span>
                      <span>{stepText}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Safety note */}
            {!isTerminal && (
              <div className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-3 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-200/90 leading-relaxed">
                  Choose <strong>PayPal USD</strong> and the <strong>Ethereum</strong> network in {meta.brand}'s withdrawal screen. Sending anything else will not be credited.
                </p>
              </div>
            )}

            {/* Reference (small, at the bottom) */}
            <p className="text-[10px] text-gray-600 text-center font-mono">
              Ref: {order.exchangeId}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

}
